/**
 * client_type_controller.js — Individual/Business toggle for client forms (Stimulus).
 *
 * One instance per form that offers the choice (data-controller="client-type").
 * Four templates host it and they are four PRESENTATIONS of one decision, not
 * four behaviours:
 *   - components/forms/client_form.html        (add client)
 *   - components/forms/quote_form.html         (new client inside a quote)
 *   - components/forms/client_edit_fields.html (native sheet edit)
 *   - components/modals/edit_client_frame.html (in-page edit modal)
 *
 * Replaces static/js/client_form.js, which was loaded per-ROUTE: a <script> tag
 * on clients.html plus membership of dashboard.bundle. Every other surface that
 * rendered these partials (/quotes, the /quotes/new and /clients/new native
 * sheets, the client detail page's edit modal) got the markup without the
 * behaviour, so the toggle checked and nothing moved. A controller ships in
 * controllers.bundle.min.js, which base.html loads unconditionally, so the
 * behaviour now travels with the markup and cannot be absent from a page that
 * renders it. Do not reintroduce a route-scoped loader for this.
 *
 * Behaviour:
 *   Individual -> first/last name required; every businessGroup (business name,
 *                 business address) hidden and its inputs cleared.
 *   Business   -> business name required, first/last become the optional
 *                 contact person (labels swap, required cleared), and the
 *                 address the form already takes is relabelled as the site.
 *
 * apply() runs on connect, on change, after a form reset, and when client-mode
 * announces it has shown or hidden this block (client-mode:applied). On the
 * quote and job forms the existing/new mode decides whether this block is live
 * at all, so the person-name flags are derived here from both: nothing is
 * required while the block is hidden, and a required field inside a
 * display:none container is one browsers refuse to submit and refuse to focus.
 * Running on connect is what gives a form that opens on "New client" (an
 * account with no clients yet) its flags without waiting for a tap.
 *
 * Label text variants come from data-label-individual / data-label-business on
 * any element inside the block (the person-name and address labels); this
 * controller holds no copy.
 */
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static targets = ['toggle', 'businessGroup', 'businessName', 'personName', 'optionalForBusiness'];

    connect() {
        // `reset` fires before the browser restores the toggle, so apply()
        // waits a task to read the restored type. The listener sits on the
        // form because this element is a block inside one on the quote and
        // job forms, and `reset` is dispatched on the form itself.
        this._form = this.element.closest('form');
        this._onReset = () => setTimeout(() => this.apply());
        this._form?.addEventListener('reset', this._onReset);
        this.apply();
    }

    disconnect() {
        this._form?.removeEventListener('reset', this._onReset);
    }

    apply() {
        const selected = this.toggleTargets.find((radio) => radio.checked);
        if (!selected) return;
        const isBusiness = selected.value === 'business';
        const active = this.element.style.display !== 'none';

        // An individual has no business name or business address, so a value
        // typed before switching type must not be posted.
        this.businessGroupTargets.forEach((group) => {
            group.classList.toggle('d-none', !isBusiness);
            if (!isBusiness) group.querySelectorAll('input').forEach((input) => { input.value = ''; });
        });
        if (this.hasBusinessNameTarget) {
            this.businessNameTarget.required = active && isBusiness;
        }

        // The person is mandatory only for an individual; on a business client
        // the same fields are an optional contact person.
        this.personNameTargets.forEach((input) => {
            input.required = active && !isBusiness;
        });
        // The client form's address: an individual's home, which they cannot be
        // added without, but a business can be added before any site is known.
        // The quote and job forms keep theirs required (client-mode owns it):
        // the document they create needs a site.
        this.optionalForBusinessTargets.forEach((input) => {
            input.required = active && !isBusiness;
        });
        this._swapLabels(isBusiness);
    }

    _swapLabels(isBusiness) {
        const key = isBusiness ? 'labelBusiness' : 'labelIndividual';
        this.element.querySelectorAll('[data-label-business]').forEach((label) => {
            const text = label.dataset[key];
            if (text) label.textContent = text;
        });
    }
}
